import { create } from "zustand";

/**
 * Artifact state (2026-07-07; tabbed workspace 2026-07-15; folded into the dock
 * 2026-07-15). Artifacts are substantial deliverables popped out of chat
 * messages — a document, an HTML page, a code file — viewed alongside the
 * conversation instead of scrolled past inline.
 *
 * **The workspace itself is gone from here.** Open/tabs/activeId/width and their
 * actions moved to `stores/dock.ts`: an artifact is now just a dock panel of
 * `kind: "artifact"`, and the dock owns cards, tabs and resizing for every kind.
 * A second parallel panel system beside the dock is precisely the thing the
 * owner's "one dock, many kinds" call was made to prevent.
 *
 * What stays is what is genuinely ARTIFACT-specific and would be dead weight in
 * a kind-blind host: how to detect/title/id/save an artifact, and the two pieces
 * of state below. The host must never grow a `view` field — a terminal has no
 * Source↔Preview.
 */

export interface Artifact {
	/** Stable id so re-opening the same block focuses its tab instead of
	 *  duplicating it. */
	id: string;
	/** Human title shown on the tab. */
	title: string;
	/** Fenced-code language hint (``html`` / ``python`` / ``markdown`` / …). */
	language: string;
	/** The raw artifact body. */
	content: string;
}

export type ArtifactView = "source" | "preview";

const AUTO_KEY = "agnt-artifact-autoopen";

function initialAutoOpen(): boolean {
	// Default ON — the flagship "artifacts auto-surface" experience. Users who
	// find it intrusive flip the toggle in the panel's toolbar (persisted here).
	if (typeof localStorage === "undefined") return true;
	return localStorage.getItem(AUTO_KEY) !== "0";
}

/** Languages the canvas can RENDER (not just syntax-highlight) in Preview. */
const PREVIEWABLE = new Set([
	"markdown", "md", "html", "htm", "xml", "svg",
]);

export function isPreviewable(language: string): boolean {
	return PREVIEWABLE.has((language || "").toLowerCase());
}

export interface ArtifactState {
	/** When true, a substantial self-contained artifact in a finalized assistant
	 *  reply auto-opens in the dock (the Claude/ChatGPT behavior). Persisted. */
	autoOpen: boolean;
	setAutoOpen: (v: boolean) => void;
	/** Source↔Preview per artifact. Keyed by artifact id — reading one document's
	 *  source must not flip the others out of preview. Deliberately NOT a field
	 *  on DockPanel: a terminal has no view mode, and the host stays kind-blind.
	 *  Absent = the default for that language, applied lazily by `viewOf`, so
	 *  opening an artifact needs no seeding round-trip through this store. */
	viewById: Record<string, ArtifactView>;
	setView: (id: string, v: ArtifactView) => void;
}

export const useArtifactStore = create<ArtifactState>((set) => ({
	autoOpen: initialAutoOpen(),
	viewById: {},
	setView: (id, v) => set((s) => ({ viewById: { ...s.viewById, [id]: v } })),
	setAutoOpen: (v) => {
		try {
			localStorage.setItem(AUTO_KEY, v ? "1" : "0");
		} catch {
			/* private mode / quota — preference just won't persist */
		}
		set({ autoOpen: v });
	},
}));

/** The view mode for an artifact: whatever it was last set to, else the default
 *  for its language — renderable content opens in Preview, code in Source. */
export function viewOf(
	s: Pick<ArtifactState, "viewById">,
	id: string,
	language: string,
): ArtifactView {
	return s.viewById[id] ?? (isPreviewable(language) ? "preview" : "source");
}

/** Stable id for an artifact from its language + content, so the same block
 *  opened twice (manual click or auto-open) is idempotent. djb2. */
export function artifactId(language: string, content: string): string {
	const s = `${language}:${content}`;
	let h = 5381;
	for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
	return (h >>> 0).toString(36);
}

const AUTO_RENDERABLE = new Set(["html", "htm", "svg", "markdown", "md"]);
const _FENCE_RE = /```([\w-]*)\n([\s\S]*?)```/g;

/**
 * Decide whether a finalized assistant reply carries a substantial
 * self-contained artifact worth auto-surfacing in the canvas. Deliberately
 * conservative to avoid popping the panel on ordinary code-snippet replies:
 *
 *  - EXACTLY ONE fenced block (multi-block replies are usually explanatory —
 *    keep them inline),
 *  - the block DOMINATES the message (≥60% of non-whitespace chars — a mostly-
 *    prose answer with a small snippet stays inline),
 *  - and it's substantial: ≥15 lines, or a renderable document (html/svg/
 *    markdown) of ≥6 lines.
 *
 * Returns the artifact to open (with a stable id + friendly title), or null.
 */
export function detectAutoArtifact(content: string): Artifact | null {
	if (!content) return null;
	_FENCE_RE.lastIndex = 0;
	const blocks: { lang: string; body: string }[] = [];
	let m: RegExpExecArray | null;
	while ((m = _FENCE_RE.exec(content)) !== null) {
		blocks.push({ lang: m[1] ?? "", body: m[2] ?? "" });
		if (blocks.length > 1) return null; // more than one block → keep inline
	}
	if (blocks.length !== 1) return null;
	const b = blocks[0]!;
	const body = b.body.replace(/\n$/, "");
	const lines = body.split("\n").length;
	const renderable = AUTO_RENDERABLE.has(b.lang.toLowerCase());
	const substantial = lines >= 15 || (renderable && lines >= 6);
	if (!substantial) return null;
	const blockChars = body.replace(/\s/g, "").length;
	const totalChars = content.replace(/\s/g, "").length;
	if (totalChars === 0 || blockChars / totalChars < 0.6) return null;
	return {
		id: artifactId(b.lang, body),
		title: artifactTitle(b.lang, body),
		language: b.lang,
		content: body,
	};
}

/**
 * Extract a reply's PRIMARY artifact — the largest fenced code block. Used by
 * artifact versioning to pull the corresponding artifact out of a regenerate
 * sibling after a branch switch (more lenient than detectAutoArtifact, which
 * only fires on a single *dominant* block). Returns null when the reply has no
 * fenced block at all.
 */
export function primaryArtifact(content: string): Artifact | null {
	if (!content) return null;
	const re = /```([\w-]*)\n([\s\S]*?)```/g;
	let best: { lang: string; body: string } | null = null;
	let bestLines = -1;
	let m: RegExpExecArray | null;
	while ((m = re.exec(content)) !== null) {
		const lang = m[1] ?? "";
		const body = (m[2] ?? "").replace(/\n$/, "");
		const lines = body.split("\n").length;
		if (lines > bestLines) {
			best = { lang, body };
			bestLines = lines;
		}
	}
	if (!best) return null;
	return {
		id: artifactId(best.lang, best.body),
		title: artifactTitle(best.lang, best.body),
		language: best.lang,
		content: best.body,
	};
}

/**
 * Pull a title out of the artifact itself — a Markdown H1, an HTML/SVG/XML
 * `<title>`. Tabs live or die on distinguishable labels: a workspace whose
 * three tabs all read "Python" can't be navigated, and the language name is all
 * we have without looking inside. Returns null when the content names itself
 * nothing useful.
 */
function derivedTitle(language: string, content: string): string | null {
	const lang = (language || "").toLowerCase();
	let m: RegExpExecArray | null = null;
	if (lang === "markdown" || lang === "md") {
		m = /^#\s+(.+)$/m.exec(content);
	} else if (lang === "html" || lang === "htm" || lang === "svg" || lang === "xml") {
		m = /<title[^>]*>([^<]+)<\/title>/i.exec(content);
	}
	const raw = m?.[1]?.trim();
	if (!raw) return null;
	return raw.length > 60 ? `${raw.slice(0, 59).trimEnd()}…` : raw;
}

/** Friendly title for a fenced block — used as the tab label, the canvas header
 *  and to seed the download filename. Prefers a title the content declares
 *  about itself; falls back to the language name. */
export function artifactTitle(language: string, content?: string): string {
	if (content) {
		const derived = derivedTitle(language, content);
		if (derived) return derived;
	}
	const lang = (language || "").toLowerCase();
	const map: Record<string, string> = {
		html: "HTML document",
		htm: "HTML document",
		svg: "SVG image",
		xml: "XML document",
		markdown: "Markdown document",
		md: "Markdown document",
		mermaid: "Mermaid diagram",
		json: "JSON",
		yaml: "YAML",
		yml: "YAML",
		css: "Stylesheet",
		sql: "SQL",
		python: "Python",
		py: "Python",
		javascript: "JavaScript",
		js: "JavaScript",
		typescript: "TypeScript",
		ts: "TypeScript",
		tsx: "TypeScript (TSX)",
		jsx: "JavaScript (JSX)",
		bash: "Shell script",
		sh: "Shell script",
	};
	return map[lang] || (lang ? `${lang} snippet` : "Artifact");
}

/** Wrap artifact content for saving as a wiki article body. Markdown is saved
 *  verbatim (it IS the article body); anything else is wrapped in a fenced code
 *  block so the wiki renders it as code, not as (mis-parsed) prose/markup. */
export function wrapForVault(content: string, language: string): string {
	const lang = (language || "").toLowerCase();
	if (lang === "markdown" || lang === "md") return content;
	return `\`\`\`${lang}\n${content}\n\`\`\`\n`;
}

/** Map a language to a download file extension. */
export function artifactExtension(language: string): string {
	const lang = (language || "").toLowerCase();
	const map: Record<string, string> = {
		html: "html", htm: "html", svg: "svg", xml: "xml",
		markdown: "md", md: "md", mermaid: "mmd", json: "json",
		yaml: "yaml", yml: "yaml", css: "css", sql: "sql",
		python: "py", py: "py", javascript: "js", js: "js",
		typescript: "ts", ts: "ts", tsx: "tsx", jsx: "jsx",
		bash: "sh", sh: "sh", text: "txt",
	};
	return map[lang] || "txt";
}
